import {
  ElfAttributes,
  ElfAttributeValue,
  ElfLogEntry,
  ElfLoggerConfig,
  ElfSeverity,
  ELF_SEVERITY_NUMBER,
  OtlpAnyValue,
  OtlpKeyValue,
  OtlpLogRecord,
  OtlpLogsPayload
} from './elf-log.model';
import { APP_VERSION, elfConfig } from './elf-config';
import { maskEmailAddress, maskMobilePhoneNumber } from '../../utils/masking';

interface DeploymentInfo {
  environment: string;
  instanceId: string;
}

// Value-based PII masking for free-text log values. Rather than the
// `basic-redact-pii` package used by one-ap-customer_web, this mirrors the
// dependency-free masking approach used in one-ap-digital-buyer-onboarding
// (src/utils/masking.ts): emails and 10-digit phone numbers found anywhere in a
// log value are passed through the shared masking helpers. Structured sensitive
// fields (tokens, tax ids, card/account numbers, etc.) are additionally masked
// by key in the HTTP interceptor before they ever reach the logger.
const EMAIL_PATTERN = /[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}/g;
const PHONE_PATTERN = /\b\d{10}\b/g;

/**
 * Client-side logger that ships structured browser logs to the ELF (Enterprise
 * Logging Framework) OTLP/HTTP logs endpoint.
 *
 * Records are POSTed directly from the browser to the environment-appropriate
 * `elfingest` host (`https://elfingest[-dev|-qa].aexp.com/ot/v1/logs`), resolved
 * at runtime from the page host. This is a cross-origin request, so elfingest
 * must allow CORS (Access-Control-Allow-Origin and the preflight OPTIONS for a
 * JSON POST) for the app origin.
 *
 * Ported from one-ap-customer_web's ElfLoggerService to a framework-agnostic
 * singleton for this React app. Logging is strictly best-effort: it must never
 * throw into application code.
 */
export class ElfLoggerService {
  private readonly config: ElfLoggerConfig;
  private readonly deployment: DeploymentInfo;
  private readonly logsEndpoint: string;
  private readonly sessionId: string;
  private readonly buffer: ElfLogEntry[] = [];
  private flushTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(config: ElfLoggerConfig = elfConfig) {
    this.config = config;
    this.deployment = this.resolveDeployment();
    this.logsEndpoint = this.resolveLogsEndpoint();
    this.sessionId = this.generateId();

    if (this.isEnabled() && typeof window !== 'undefined') {
      // Flush anything still buffered when the tab is backgrounded or closed.
      window.addEventListener('visibilitychange', this.onVisibilityChange, true);
      window.addEventListener('pagehide', this.onPageHide, true);
    }
  }

  debug(message: string, attributes?: ElfAttributes): void {
    this.log(ElfSeverity.DEBUG, message, attributes);
  }

  info(message: string, attributes?: ElfAttributes): void {
    this.log(ElfSeverity.INFO, message, attributes);
  }

  warn(message: string, attributes?: ElfAttributes): void {
    this.log(ElfSeverity.WARN, message, attributes);
  }

  error(message: string, attributes?: ElfAttributes): void {
    this.log(ElfSeverity.ERROR, message, attributes);
  }

  fatal(message: string, attributes?: ElfAttributes): void {
    this.log(ElfSeverity.FATAL, message, attributes);
  }

  /** Convenience helper for logging a caught/uncaught error object. */
  logException(error: unknown, attributes?: ElfAttributes): void {
    const err = error as { message?: unknown; name?: unknown; stack?: unknown } | null;
    const message = err && err.message != null ? this.safeString(err.message) : this.safeString(error);
    this.log(ElfSeverity.ERROR, message, {
      ...attributes,
      'error.type': err && err.name != null ? this.safeString(err.name) : 'Error',
      'exception.stacktrace': err && typeof err.stack === 'string' ? err.stack : undefined
    });
  }

  log(severity: ElfSeverity, message: string, attributes?: ElfAttributes): void {
    try {
      if (!this.isEnabled()) {
        return;
      }
      const entry: ElfLogEntry = {
        timeUnixNano: this.nowUnixNano(),
        severity,
        body: this.redact(this.safeString(message)),
        attributes: this.redactAttributes({ ...this.baseAttributes(), ...(attributes || {}) })
      };
      this.buffer.push(entry);

      const isHighPriority = severity === ElfSeverity.ERROR || severity === ElfSeverity.FATAL;
      if (isHighPriority || this.buffer.length >= this.config.batchSize) {
        this.flush();
      } else {
        this.scheduleFlush();
      }
    } catch {
      // Logging must never break the application.
    }
  }

  private onVisibilityChange = (): void => {
    if (typeof document !== 'undefined' && document.visibilityState === 'hidden') {
      this.flushViaBeacon();
    }
  };

  private onPageHide = (): void => {
    this.flushViaBeacon();
  };

  private isEnabled(): boolean {
    return !!this.config && this.config.enabled === true && !!this.logsEndpoint;
  }

  private scheduleFlush(): void {
    if (this.flushTimer != null) {
      return;
    }
    this.flushTimer = setTimeout(() => {
      this.flushTimer = null;
      this.flush();
    }, this.config.flushIntervalMs);
  }

  private flush(): void {
    this.clearTimer();
    if (!this.buffer.length) {
      return;
    }
    const batch = this.buffer.splice(0, this.buffer.length);
    this.send(JSON.stringify(this.buildPayload(batch)));
  }

  private flushViaBeacon(): void {
    this.clearTimer();
    if (!this.buffer.length) {
      return;
    }
    const batch = this.buffer.splice(0, this.buffer.length);
    this.beacon(JSON.stringify(this.buildPayload(batch)));
  }

  private clearTimer(): void {
    if (this.flushTimer != null) {
      clearTimeout(this.flushTimer);
      this.flushTimer = null;
    }
  }

  private send(body: string): void {
    try {
      if (typeof fetch === 'function') {
        // keepalive lets the request outlive a navigation without needing a beacon.
        // credentials 'omit': never send cookies cross-origin to elfingest.
        fetch(this.logsEndpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body,
          keepalive: true,
          credentials: 'omit'
        }).catch(() => this.beacon(body));
        return;
      }
      this.beacon(body);
    } catch {
      this.beacon(body);
    }
  }

  private beacon(body: string): void {
    try {
      if (typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
        const blob = new Blob([body], { type: 'application/json' });
        navigator.sendBeacon(this.logsEndpoint, blob);
      }
    } catch {
      // ignore - best effort only
    }
  }

  private buildPayload(entries: ElfLogEntry[]): OtlpLogsPayload {
    return {
      resourceLogs: [
        {
          resource: { attributes: this.resourceAttributes() },
          scopeLogs: [
            {
              scope: {
                name: this.config.scopeName,
                version: this.config.scopeVersion || APP_VERSION
              },
              logRecords: entries.map((entry) => this.toLogRecord(entry))
            }
          ]
        }
      ]
    };
  }

  private toLogRecord(entry: ElfLogEntry): OtlpLogRecord {
    return {
      timeUnixNano: entry.timeUnixNano,
      observedTimeUnixNano: entry.timeUnixNano,
      severityNumber: ELF_SEVERITY_NUMBER[entry.severity],
      severityText: entry.severity,
      body: { stringValue: entry.body },
      attributes: this.toKeyValues(entry.attributes)
    };
  }

  private resourceAttributes(): OtlpKeyValue[] {
    return this.toKeyValues({
      'service.name': this.config.serviceName,
      'service.namespace': this.config.serviceNamespace,
      'service.instance.id': this.deployment.instanceId,
      'service.car.id': this.config.carId,
      'deployment.environment': this.deployment.environment,
      'telemetry.sdk.language': 'webjs',
      'telemetry.sdk.name': 'one-ap-buyer-onboarding-web-elf'
    });
  }

  private baseAttributes(): ElfAttributes {
    const attributes: ElfAttributes = {
      'session.id': this.sessionId,
      'app.version': APP_VERSION
    };
    if (typeof window !== 'undefined' && window.location) {
      attributes['url.path'] = window.location.pathname;
    }
    if (typeof navigator !== 'undefined' && navigator.userAgent) {
      attributes['user_agent.original'] = navigator.userAgent;
    }
    return attributes;
  }

  private toKeyValues(attributes: ElfAttributes): OtlpKeyValue[] {
    const result: OtlpKeyValue[] = [];
    Object.keys(attributes || {}).forEach((key) => {
      const value = attributes[key];
      if (value === undefined || value === null) {
        return;
      }
      result.push({ key, value: this.toAnyValue(value) });
    });
    return result;
  }

  private toAnyValue(value: ElfAttributeValue): OtlpAnyValue {
    if (typeof value === 'boolean') {
      return { boolValue: value };
    }
    if (typeof value === 'number') {
      return Number.isInteger(value) ? { intValue: String(value) } : { doubleValue: value };
    }
    return { stringValue: this.safeString(value) };
  }

  private nowUnixNano(): string {
    // Nanosecond precision would overflow JS number safety, so keep ms precision
    // and pad to nanoseconds as a string (int64 on the wire).
    return `${Date.now()}000000`;
  }

  private resolveDeployment(): DeploymentInfo {
    const host =
      typeof window !== 'undefined' && window.location && window.location.hostname
        ? window.location.hostname
        : '';
    // Mirrors the helm OTEL_RESOURCE_ATTRIBUTES service.instance.id per env so
    // client + server logs align.
    if (host.includes('dev') || host.includes('e1demo') || host.includes('localhost')) {
      return { environment: 'E1', instanceId: 'oneaponboarding-dev.americanexpress.com' };
    }
    if (
      host.includes('stg') ||
      host.includes('e2') ||
      host.includes('demo') ||
      host.includes('test') ||
      host.includes('qa')
    ) {
      return { environment: 'E2', instanceId: 'oneaponboarding-qa.americanexpress.com' };
    }
    return { environment: 'E3', instanceId: 'oneaponboarding.americanexpress.com' };
  }

  /**
   * Full OTLP/HTTP logs URL for the current environment. The browser POSTs here
   * directly, so this is the real cross-origin destination (elfingest), not a
   * same-origin path. Reuses the env resolved in resolveDeployment().
   */
  private resolveLogsEndpoint(): string {
    const ingestByEnv: { [env: string]: string } = {
      E1: 'https://elfingest-dev.aexp.com',
      E2: 'https://elfingest-qa.aexp.com',
      E3: 'https://elfingest.aexp.com'
    };
    const ingest = ingestByEnv[this.deployment.environment] || ingestByEnv.E3;
    return `${ingest}/ot/v1/logs`;
  }

  private redactAttributes(attributes: ElfAttributes): ElfAttributes {
    const redacted: ElfAttributes = {};
    Object.keys(attributes).forEach((key) => {
      const value = attributes[key];
      redacted[key] = typeof value === 'string' ? this.redact(value) : value;
    });
    return redacted;
  }

  private redact(input: string): string {
    if (!input) {
      return input;
    }
    try {
      return input
        .replace(EMAIL_PATTERN, (match) => maskEmailAddress(match))
        .replace(PHONE_PATTERN, (match) => maskMobilePhoneNumber(match));
    } catch {
      return input;
    }
  }

  private safeString(value: unknown): string {
    if (typeof value === 'string') {
      return value;
    }
    if (value == null) {
      return '';
    }
    try {
      return JSON.stringify(value);
    } catch {
      return String(value);
    }
  }

  private generateId(): string {
    try {
      const cryptoObj = typeof crypto !== 'undefined' ? (crypto as Crypto & { randomUUID?: () => string }) : undefined;
      if (cryptoObj && typeof cryptoObj.randomUUID === 'function') {
        return cryptoObj.randomUUID();
      }
    } catch {
      // fall through to the manual generator
    }
    return `xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx`.replace(/[xy]/g, (char) => {
      const rand = (Math.random() * 16) | 0;
      const value = char === 'x' ? rand : (rand & 0x3) | 0x8;
      return value.toString(16);
    });
  }
}

/** App-wide singleton, mirroring the Angular `providedIn: 'root'` service. */
export const elfLogger = new ElfLoggerService();
